import { FC, useMemo, useState } from "react";
import { Box, Button, Chip, Divider, IconButton, MenuItem, styled } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faChevronDown, faChevronUp, faObjectGroup, faTableList } from "@fortawesome/duotone-light-svg-icons";
import { Checkbox, InputOnlyField, Markdown, Text, Numeric, Select } from "@ngt/forms";
import ConfigurationFormEditPanel, { ConfigurationBox } from './ConfigurationFormEditPanel';
import { ConfigurationType, IConfigurationSection } from "@/utilities/forms/ConfigurationFormProcessor";
import { IConfigurationFormEditContext } from "@/contexts/forms/ConfigurationFormEditContext";
import ConfigurationFormEditInfo, { ConfigurationDividerBox, ConfigurationFlexBox, ConfigurationTitle, OtherVersionsBox, OtherVersionsItem } from "./ConfigurationFormEditInfo";
import { useFormActions } from "@ngt/forms-core";
import { IEventMetadata, IFormMetadata, IFormSectionMetadata, IFormVersionMetadata, ISourceDataConfigurationFormSection, SourceDataPriority } from "@/api/dtos";
import ConfigurationFormEditCopy from "./ConfigurationFormEditCopy";
import ConfigurationFormEditUpdate from "./ConfigurationFormEditUpdate";
import { faExclamationTriangle, faTriangle } from "@fortawesome/pro-solid-svg-icons";

export interface IConfigurationFormEditSectionProps {
    context: IConfigurationFormEditContext<IConfigurationSection>
}

interface IOtherVersion {
    type: ConfigurationType;
    eventKey?: string;
    eventMetadata?: IEventMetadata;
    formKey: string;
    formMetadata: IFormMetadata;
    formVersionKey: string;
    formVersionMetadata: IFormVersionMetadata;
    formSectionKey: string;
    formSectionMetadata: IFormSectionMetadata;
    formSectionConfiguration: ISourceDataConfigurationFormSection;
    configurationPropertyPath: string;
    linked: boolean;
    isGlobal: boolean;
    isTrial: boolean;
}

const ConfigurationFormEditSection: FC<IConfigurationFormEditSectionProps> = ({
    context
}) => {
    const {
        configurationPropertyPath,
        item: {
            formSectionMetadata
        }
    } = context;
    const [openOtherVersions, setOpenOtherVersions] = useState(false);
    
    const actions = useFormActions();

    const allVersions = useMemo(() => {
        if (!context.item.formMetadata?.versions || context.usingTrialConfiguration) {
            return null;
        }

        let currentVersion: IOtherVersion | undefined = undefined;

        const versionKeys = context.item.formMetadata.versions.map(x => x.id);

        const versions: IOtherVersion[] = [];

        const sectionPartialPath = context.item.formSectionKey ? `.sections.${context.item.formSectionKey}` : '';

        for (let versionKey of versionKeys) {
            const versionMetadata = context.item.formMetadata?.versions?.find(x => x.id === versionKey);
            const sectionMetadata = versionMetadata?.sections?.find(x => x.id === context.item.formSectionKey);

            if (context.item.formEvents) {
                for (let event of context.item.formEvents[context.item.formKey!]) {
                    const localPropertyPath = `configuration.events.${event.eventKey}.forms.${context.item.formKey!}.versions.${versionKey}${sectionPartialPath}`
                    const localFormVersion = actions.getFieldValue<ISourceDataConfigurationFormSection>(localPropertyPath);

                    if (localFormVersion) {
                        const data = {
                            type: ConfigurationType.Section,
                            eventKey: event.eventKey,
                            eventMetadata: event.eventMetadata,
                            formKey: context.item.formKey!,
                            formMetadata: context.item.formMetadata!,
                            formVersionKey: versionKey!,
                            formVersionMetadata: versionMetadata!,
                            formSectionKey: context.item.formSectionKey!,
                            formSectionMetadata: sectionMetadata!,
                            formSectionConfiguration: localFormVersion,
                            configurationPropertyPath: localPropertyPath,
                            linked: !!localFormVersion?.linked,
                            isGlobal: false,
                            isTrial: !!context.item.isTrialConfiguration
                        }

                        if (context.configurationPropertyPath !== localPropertyPath) {
                            versions.push(data)
                        }
                        else {
                            currentVersion = data;
                        }
                    }
                }
            }

            const globalPropertyPath = `configuration.forms.${context.item.formKey!}.versions.${versionKey}${sectionPartialPath}`;
            const globalFormVersion = actions.getFieldValue<ISourceDataConfigurationFormSection>(globalPropertyPath);
            
            if (globalFormVersion) {
                const data = {
                    type: ConfigurationType.Section,
                    eventKey: undefined,
                    eventMetadata: undefined,
                    formKey: context.item.formKey!,
                    formMetadata: context.item.formMetadata!,
                    formVersionKey: versionKey!,
                    formVersionMetadata: versionMetadata!,
                    formSectionKey: context.item.formSectionKey!,
                    formSectionMetadata: sectionMetadata!,
                    formSectionConfiguration: globalFormVersion,
                    configurationPropertyPath: globalPropertyPath,
                    linked: !!globalFormVersion?.linked,
                    isGlobal: !context.item.isTrialConfiguration,
                    isTrial: !!context.item.isTrialConfiguration
                }

                if (context.configurationPropertyPath !== globalPropertyPath) {
                    versions.push(data)
                }
                else {
                    currentVersion = data;
                }
            }

            const trialPropertyPath = `trialConfiguration.forms.${context.item.formKey!}.versions.${versionKey}${sectionPartialPath}`
            const trialFormVersion = actions.getFieldValue<ISourceDataConfigurationFormSection>(trialPropertyPath);
            
            if (trialFormVersion) {
                const data = {
                    type: ConfigurationType.Section,
                    eventKey: undefined,
                    eventMetadata: undefined,
                    formKey: context.item.formKey!,
                    formMetadata: context.item.formMetadata!,
                    formVersionKey: versionKey!,
                    formVersionMetadata: versionMetadata!,
                    formSectionKey: context.item.formSectionKey!,
                    formSectionMetadata: sectionMetadata!,
                    formSectionConfiguration: trialFormVersion,
                    configurationPropertyPath: trialPropertyPath,
                    linked: !!trialFormVersion?.linked,
                    isGlobal: false,
                    isTrial: true
                }

                if (context.configurationPropertyPath !== trialPropertyPath) {
                    versions.push(data)
                }
                else {
                    currentVersion = data;
                }
            }


        }

        if (versions.length === 0) {
            return null;
        }

        return {
            otherVersions: versions.sort((a, b) => {

                if (a.eventKey !== b.eventKey) {
                    return (a.eventKey ?? 'zzz') < (b.eventKey ?? 'zzz') ? -1 : 1;
                }
                
                if (a.formKey !== b.formKey) {
                    return a.formKey < b.formKey ? -1 : 1;
                }
                
                if (a.formVersionKey !== b.formVersionKey) {
                    return a.formVersionKey < b.formVersionKey ? -1 : 1;
                }
                
                if (a.formSectionKey !== b.formSectionKey) {
                    return (a.formSectionKey) < (b.formSectionKey) ? -1 : 1;
                }
                
                return -1;
            }),
            currentVersion
        };
    }, [context.item.formMetadata?.versions, context.item, context.usingTrialConfiguration, context.usingGlobalConfiguration, actions])

    const otherVersionContent = useMemo(() => {

        if (!allVersions || !allVersions.otherVersions) {
            return null;
        }

        return (
            <OtherVersionsBox>
                {
                    allVersions.otherVersions.map((x, i, a) => {            
                        return (
                            <OtherVersionsItem key={x.configurationPropertyPath}>
                                <ConfigurationFormEditInfo 
                                    {...x}
                                    dividers
                                />
                                <ConfigurationDividerBox>
                                    <Divider />
                                </ConfigurationDividerBox>
                                <ConfigurationFlexBox sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    {
                                        !!allVersions.currentVersion && (
                                            <>
                                                <ConfigurationFormEditCopy 
                                                    target={allVersions.currentVersion}
                                                    source={x}
                                                />
                                                <ConfigurationFormEditUpdate 
                                                    target={x}
                                                    source={allVersions.currentVersion}
                                                />
                                            </>
                                        )
                                    }
                                </ConfigurationFlexBox>
                            </OtherVersionsItem>
                        )
                    })
                }
            </OtherVersionsBox>
        )
    }, [allVersions, context.item]);
    
    return (
        <ConfigurationFormEditPanel 
            context={context} 
            type={formSectionMetadata?.repeating ? 'Table' : 'Group'}
            typeIcon={formSectionMetadata?.repeating ? faTableList : faObjectGroup}
        >
            
            {
                allVersions?.otherVersions && (
                    <>
                        <ConfigurationTitle 
                            variant="h2" 
                            {...{component: Button} as any} 
                            color='inherit' 
                            sx={{ 
                                display: 'flex', 
                                padding: 0,
                                textTransform: 'none'
                            }}
                            onClick={() => setOpenOtherVersions(c => !c)}
                        >
                            Other Versions
                            <Chip 
                                label={allVersions.otherVersions.length}
                                color="primary"
                                sx={{
                                    ml: 'auto'
                                }}
                                size="small"
                            />
                            <IconButton component="div">
                                <FontAwesomeIcon icon={openOtherVersions ? faChevronUp : faChevronDown} />
                            </IconButton>
                        </ConfigurationTitle>
                        {
                            openOtherVersions && (
                                otherVersionContent
                            )
                        }
                    </>
                )
            }
            <ConfigurationTitle variant="h2">
                Options
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Override Name"
                    propertyPath={`${configurationPropertyPath}.name`}
                    component={Text}
                    margin="normal"
                />
                <InputOnlyField 
                    label="Priority"
                    propertyPath={`${configurationPropertyPath}.priority`}
                    component={Select}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                >
                    <MenuItem
                        value={SourceDataPriority.Low}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            Low 
                            <Box sx={{ml: 'auto', color: 'success.main', transform: 'rotate(180deg)'}}>
                                <FontAwesomeIcon icon={faTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.Normal}
                    >
                        Normal
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.High}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            High
                            <Box sx={{ml: 'auto', color: 'warning.main'}}>
                                <FontAwesomeIcon icon={faTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.Critical}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            Critical
                            <Box sx={{ml: 'auto', color: 'error.main'}}>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                </InputOnlyField>
                <InputOnlyField 
                    label="Hide Section When No Data Provided"
                    propertyPath={`${configurationPropertyPath}.excludeBlank`}
                    component={Checkbox}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                />
            </ConfigurationBox>
            
            {
                formSectionMetadata?.repeating && (
                    <>
                        <ConfigurationTitle variant="h4">
                            Collection
                        </ConfigurationTitle>
                        <ConfigurationBox>
                            <InputOnlyField
                                label="Lastest Repeat Only"
                                propertyPath={`${configurationPropertyPath}.latestRepeat`}
                                component={Checkbox}
                                FormControlProps={{
                                    margin: 'normal'
                                }}
                            />
                            <InputOnlyField
                                label="Minimum Repeat"
                                propertyPath={`${configurationPropertyPath}.minimumRepeat`}
                                component={Numeric}
                                margin="normal"
                            />
                            <InputOnlyField
                                label="Maximum Repeat"
                                propertyPath={`${configurationPropertyPath}.maximumRepeat`}
                                component={Numeric}
                                margin="normal"
                            />
                        </ConfigurationBox>
                    </>
                )
            }

            <ConfigurationTitle variant="h4">
                Alerts
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Information"
                    propertyPath={`${configurationPropertyPath}.alertInformation`}
                    component={Markdown}
                    margin="normal"
                    //color="info"
                />
                <InputOnlyField 
                    label="Warning"
                    propertyPath={`${configurationPropertyPath}.alertWarning`}
                    component={Markdown}
                    margin="normal"
                    //color="warning"
                />
                <InputOnlyField 
                    label="Error"
                    propertyPath={`${configurationPropertyPath}.alertError`}
                    component={Markdown}
                    margin="normal"
                    //color="error"
                />
                <InputOnlyField 
                    label="Success"
                    propertyPath={`${configurationPropertyPath}.alertSuccess`}
                    component={Markdown}
                    margin="normal"
                    //color="success"
                />
            </ConfigurationBox>
        </ConfigurationFormEditPanel>
    )
}

export default ConfigurationFormEditSection;